import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import adminApi from "./adminApi";
import { StatusBadge } from "./StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calendar, Mail, Image, Package } from "lucide-react";

const DashboardOverview = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        adminApi.get(`${API}/stats`),
        adminApi.get(`${API}/bookings`),
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      logger.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const statCards = [
    { id: "bookings", label: t("admin.dashboard.totalBookings"), value: stats?.bookings?.total || 0, icon: Calendar, color: "primary", subtitle: t("admin.dashboard.pendingCount", { count: stats?.bookings?.pending || 0 }) },
    { id: "inquiries", label: t("admin.dashboard.inquiries"), value: stats?.inquiries?.total || 0, icon: Mail, color: "secondary", subtitle: t("admin.dashboard.unreadCount", { count: stats?.inquiries?.unread || 0 }) },
    { id: "packages", label: t("admin.dashboard.activePackages"), value: stats?.packages || 0, icon: Package, color: "accent", subtitle: t("admin.dashboard.available") },
    { id: "gallery", label: t("admin.dashboard.galleryItems"), value: stats?.gallery || 0, icon: Image, color: "primary", subtitle: t("admin.dashboard.photos") },
  ];

  return (
    <div data-testid="dashboard-overview" className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.title")}</h1>
        <p className="text-[#6B705C]">{t("admin.dashboard.welcomeMessage")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.id} className="bg-white border-0 shadow-sm" data-testid={`stat-card-${stat.id}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`admin-stat-card icon ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-[#6B705C]">{stat.label}</p>
                  <p className="text-2xl font-bold text-[#3D405B]">{stat.value}</p>
                  <p className="text-xs text-[#6B705C]">{stat.subtitle}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl text-[#3D405B]">{t("admin.dashboard.recentBookings")}</CardTitle>
        </CardHeader>
        <CardContent>
          {recentBookings.length === 0 ? (
            <p className="text-[#6B705C] text-center py-8">{t("admin.dashboard.noBookings")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.dashboard.table.customer")}</TableHead>
                  <TableHead>{t("admin.dashboard.table.event")}</TableHead>
                  <TableHead>{t("admin.dashboard.table.date")}</TableHead>
                  <TableHead>{t("admin.dashboard.table.guestCount")}</TableHead>
                  <TableHead>{t("admin.dashboard.table.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBookings.map((booking) => (
                  <TableRow key={booking.id} data-testid={`recent-booking-${booking.id}`}>
                    <TableCell className="font-medium">{booking.customer_name}</TableCell>
                    <TableCell>{booking.event_type}</TableCell>
                    <TableCell>{booking.event_date}</TableCell>
                    <TableCell>{booking.guest_count}</TableCell>
                    <TableCell><StatusBadge status={booking.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
